import { useState, useContext, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";

import { UserAuth } from "../../utils/AuthContext";
import { onlyNums } from "../../utils/OnlyNums";
import { formatDate } from "../../utils/toDate";

import classes from "../../components/Modal.module.css";

import saveIcon from "../../assets/save_icon.png";
import closeIcon from "../../assets/close_icon.png";

import Modal from "../../components/Modal";
import CustomInput from "../../components/CustomInput";
import SectionDivider from "../../components/SectionDivider";
import Dropdown from "../../components/Dropdown";
import DiscountSection from "../../components/DiscountSection";
import CustomInputWithUnit from "../../components/CustomInputWithUnit";
import HotCustomInput from "../../components/HotCustomInput";
import Loader from "../../components/Loader";
import CustomButton from "../../components/CustomButton";
import { sanitizeNumber } from "../../utils/sanitizeNumber";

const AddAppointmentModal = ({
  handleShowAddAppointment,
  handleNewAppointmentAdded,
  setShowModal,
}) => {
  const { t } = useTranslation("general");

  const { userData } = useContext(UserAuth);

  const getPatients = async (page, limit, isDropDown, search) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_URL}/getAllPatients`,
        {
          params: {
            page,
            limit,
            organizationId: userData?.organizationId,
            search,
          },
        }
      );

      return response;
    } catch (error) {
      console.error("Error fetching patients:", error);
      throw error;
    }
  };

  const [selectedCustomer, setSelectedCustomer] = useState({
    id: "",
    name: "",
    phoneNumber: "",
    age: "",
  });

  // appointment state
  const [appointment, setNewAppointment] = useState({
    userId: null,
    organizationId: null,

    doctorName: "",
    price: null,
    date: formatDate(new Date()),
    time: "",
    notes: "",
  });

  // treatment state
  const [errorFields, setErrorFields] = useState({});
  const [treatments, setTreatments] = useState([]);
  const [subTreatments, setSubTreatments] = useState([]);
  const [selectedTreatment, setSelectedTreatment] = useState(null);
  const [selectedSubTreatment, setSelectedSubTreatment] = useState(null);
  const [loadingModal, setLoadingModal] = useState(false);
  const [price, setPrice] = useState(0);
  const [originalPrice, setOriginalPrice] = useState(0);

  const validateFields = () => {
    let errors = {};
    let hasError = false;

    // Validate Customer Name
    if (!selectedCustomer?.name.trim()) {
      errors["name"] = t("emptyInputField");
      hasError = true;
    }

    // Validate Customer Phone Number
    if (
      !selectedCustomer?.phoneNumber?.trim() ||
      !/^\d+$/.test(selectedCustomer.phoneNumber)
    ) {
      errors["phoneNumber"] = t("validPhoneNumberRequired");
      hasError = true;
    }

    // Validate Customer Age
    if (selectedCustomer?.age <= 0) {
      errors["age"] = t("validAgeRequired");
      hasError = true;
    }

    // Validate Doctor Name
    if (!selectedDoctor) {
      errors["doctorName"] = t("doctorNameRequired");
      hasError = true;
    }

    // Validate Medical Treatment
    if (!selectedTreatment) {
      errors["treatmentName"] = t("medicalTreatmentRequired");
      hasError = true;
    }

    // Validate Price
    if (!parseFloat(sanitizeNumber(price))) {
      errors["price"] = t("validPriceRequired");
      hasError = true;
    }

    // Validate Appointment Date
    if (!appointment.date) {
      errors["date"] = t("validDateRequired");
      hasError = true;
    }

    // Validate Appointment Time
    if (!appointment.time) {
      errors["time"] = t("validTimeRequired");
      hasError = true;
    }

    setErrorFields(errors);
    return !hasError;
  };

  const handleCancelModal = () => {
    setNewAppointment({});
    handleShowAddAppointment(false);
  };

  const handlePriceChange = (e) => {
    const priceValue = e.target.value;
    setPrice(priceValue);
    setOriginalPrice(priceValue);
  };

  const handleInputChange = (e, state = "appointment") => {
    let { name, value } = e.target;

    if (state === "selectedCustomer") {
      if (name === "age") {
        value = parseInt(value);
      }
      setSelectedCustomer((prev) => ({ ...prev, [name]: value }));
    }

    setNewAppointment((prevAppointment) => ({
      ...prevAppointment,
      [name]: value,
    }));
  };

  // doctor state
  const [allDoctors, setAllDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState("");

  const getAllTreatments = async (page, limit, searchTerm = "") => {
    try {
      const token = userData?.token;
      if (!token) {
        console.error("Token is missing, aborting request.");
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_URL}/getAllMedicalTreatments`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          params: {
            page: page || 1,
            limit: limit ?? 10,
            search: searchTerm,
            organizationId: userData?.organizationId,
          },
        }
      );

      const treatmentsData = response.data.data;

      setTreatments(treatmentsData);
    } catch (error) {
      console.error(
        "Error fetching treatments:",
        error.response?.data || error
      );
    }
  };

  // appointment modal functions
  const createNewAppointment = async () => {
    if (!validateFields()) {
      console.error("Invalid input", errorFields);
      return;
    }

    try {
      setLoadingModal(true);
      const token = userData?.token;
      const userId = parseInt(userData.userId, 10);

      const organizationId = parseInt(
        localStorage.getItem("organizationId"),
        10
      );

      // if (!userId) {
      //   console.error("User ID not found in localStorage");
      //   return;
      // }

      const payload = {
        ...appointment,
        customerName: selectedCustomer?.name,
        customerPhoneNumber: selectedCustomer?.phoneNumber,
        customerAge: selectedCustomer?.age,
        patientId: selectedCustomer?.id,
        userId,
        organizationId,
        priceAfterDiscount: parseFloat(sanitizeNumber(price)) || 0,
        price: parseFloat(sanitizeNumber(originalPrice)),

        // subMedicalTreatment name || Treatment name
        treatmentName: selectedTreatment?.name,
        subTreatmentName: selectedSubTreatment?.name,
        doctorName: selectedDoctor.name,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_URL}/createAppointment`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status == 201) {
        handleNewAppointmentAdded((prev) => !prev);
        handleShowAddAppointment(false);
      }
      setShowModal(false);
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingModal(false);
    }
  };

  // function to get all the doctors
  const getAllDoctors = async () => {
    const token = localStorage.getItem("token");
    const userId = parseInt(localStorage.getItem("userId"), 10);
    const organizationId = userData?.organizationId;

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_URL}/getUsers`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "User-Id": parseInt(userId),
            "organization-Id": parseInt(organizationId),
          },
          params: {
            page: 1,
            limit: 10,
            search: "",
            role: "Doctor",
          },
        }
      );

      if (response?.status === 200) {
        const doctors = response.data.users
          .filter((user) => user?.role === "Doctor" || user?.role === "Healer")
          .map((user) => ({
            id: user.id,
            name: user.username || user.email,
            email: user.email,
            role: user.role,
            specialty: user.specialty,
          }));

        setAllDoctors(doctors);
      }
    } catch (error) {
      console.log("all doctors", error);
    }
  };

  useEffect(() => {
    getAllTreatments();
    getAllDoctors();
  }, []);

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
  };

  return (
    <Modal
      title="addAppointment"
      onClose={handleCancelModal}
      footer={
        <>
          {loadingModal ? <Loader /> : null}
          <section className={classes.actions}>
            <CustomButton
              onClick={createNewAppointment}
              title={t("confirm")}
              icon={saveIcon}
              iconSize={16}
              disabled={loadingModal}
            />
            <CustomButton
              title={t("cancel")}
              onClick={handleCancelModal}
              variant="blank"
              icon={closeIcon}
              iconSize={14}
            />
          </section>
        </>
      }
    >
      <div className={classes.flexableForm}>
        {/* patient details */}
        <SectionDivider title={"patientDetails"} />
        <section>
          <div className={classes.inputContainer}>
            <span>{t("patientName")}: </span>

            <HotCustomInput
              initOptions={[]}
              selected={selectedCustomer}
              setSelected={(customer) =>
                handleSelectCustomer(customer, "noRequest")
              }
              optionsName="patients"
              getData={getPatients}
              flex
            />
          </div>
        </section>

        <section className={classes.section}>
          <div className={classes.inputContainer}>
            <span>{t("patientAge")}: </span>
            <CustomInput
              name="age"
              type="number"
              flex={true}
              value={selectedCustomer?.age}
              onChange={(e) => handleInputChange(e, "selectedCustomer")}
              errorText={errorFields["age"]}
            />
          </div>
        </section>

        <section className={classes.section}>
          <div className={classes.inputContainer}>
            <span>{t("phoneNumber")}: </span>
            <CustomInput
              name="phoneNumber"
              type="text"
              flex={true}
              value={selectedCustomer?.phoneNumber}
              onChange={(e) => handleInputChange(e, "selectedCustomer")}
              errorText={errorFields["phoneNumber"]}
            />
          </div>
        </section>

        {/* appointment details */}
        <SectionDivider title={"appointmentDetails"} />
        <section>
          <div className={classes.inputContainer}>
            <span>{t("healerName")}: </span>
            <Dropdown
              options={allDoctors}
              selected={selectedDoctor}
              setSelected={(value) => {
                setSelectedDoctor(value);
              }}
              flex
              errorText={errorFields["doctorName"]}
            />
          </div>
        </section>

        <section className={classes.section}>
          <div className={classes.inputContainer}>
            <span>{t("Date")}: </span>
            <CustomInput
              name="date"
              type="date"
              flex={true}
              value={appointment?.date}
              onChange={handleInputChange}
              errorText={errorFields["date"]}
            />
          </div>
        </section>

        {/* Appointment Type Dropdown */}
        <section className={classes.section}>
          <div className={classes.inputContainer}>
            <span>{t("treatmentType")}: </span>
            <Dropdown
              options={treatments}
              selected={selectedTreatment}
              setSelected={(value) => {
                setSelectedTreatment(value);
                setSubTreatments(value.subMedicalTreatment);
                setSelectedSubTreatment(null);
                setPrice(value?.treatmentPrice || 0);
                const newPrice = value?.treatmentPrice || 0;
                setOriginalPrice(newPrice);
              }}
              flex
              errorText={errorFields["treatmentName"]}
            />
          </div>
        </section>

        {/* Sub-dropdown for Laser Type */}
        {subTreatments?.length > 0 && (
          <section className={classes.section}>
            <div className={classes.inputContainer}>
              <span>{t("subTreatmentType")}: </span>
              <Dropdown
                options={subTreatments}
                selected={selectedSubTreatment}
                setSelected={(value) => {
                  setSelectedSubTreatment(value);
                  const newPrice = value.price;
                  setOriginalPrice(newPrice);
                  setPrice(newPrice);
                }}
                flex
              />
            </div>
          </section>
        )}

        {/* Price in IQD and Discount */}
        {true && (
          <>
            <section className={classes.section}>
              <div className={classes.inputContainer}>
                <span>{t("price")}: </span>
                <div
                  className={`o-row o-row--horizontal-align_space-between o-row--gap-size_400 o-row--vertical-align_center u-width_full-width`}
                >
                  <CustomInputWithUnit
                    name="price"
                    value={originalPrice}
                    onChange={handlePriceChange}
                    flex={true}
                    unit="IQD"
                    errorText={errorFields["price"]}
                    type="money"
                  />
                </div>
              </div>
            </section>

            <section className={classes.section}>
              <DiscountSection
                originalPrice={originalPrice}
                // onPriceChange={(priceValue) => setPrice(priceValue)}
                onPriceChange={(priceValue, discountType, discountAmount) => {
                  setPrice(priceValue);
                  setNewAppointment((prev) => ({
                    ...prev,
                    discountType,
                    discountAmount,
                  }));
                }}
              />
            </section>
          </>
        )}
      </div>

      <section className={classes.section}>
        <div className={classes.inputContainer}>
          <span>{t("time")}: </span>
          <CustomInput
            name="time"
            type="time"
            flex={true}
            value={appointment?.time}
            onChange={handleInputChange}
            errorText={errorFields["time"]}
          />
        </div>
      </section>

      <section className={classes.section}>
        <div className={classes.inputContainer}>
          <span>{t("notes")}: </span>
          <CustomInput
            name="notes"
            type="textarea"
            flex={true}
            value={appointment?.notes}
            onChange={handleInputChange}
          />
        </div>
      </section>
    </Modal>
  );
};

export default AddAppointmentModal;
